/**
 * View-provenance line for the dock exports (Excel + PDF).
 *
 * Export semantics: the KPI band always mirrors the ON-SCREEN strip — full view scope (search +
 * time window, but NEVER the KPI quick-filter, which only narrows the grid below) — while the
 * data table is WYSIWYG (exactly the visible rows). When any narrowing is active those two
 * universes differ, so the export carries one explicit line saying what the table was filtered
 * to, e.g.:  "View filtered: Critical · 46 of 127 rows · window: Last 7 days".
 * No filter active → empty string → the exporters print nothing (clean default output).
 */
export interface ExportViewDesc {
  /** Active KPI quick-filter label — legacy single-chip form (null/'' when none). */
  kpiLabel?: string | null;
  /** ALL active KPI chips (multi-select, AND-combined) — listed joined with " + ",
   *  e.g. "Critical + Expired". Takes precedence over kpiLabel when non-empty. */
  kpiLabels?: string[] | null;
  /** Quick-search text, including field:value facets (null/'' when none). */
  search?: string | null;
  /** Time-window label when not "All time" (null/'' otherwise). */
  windowLabel?: string | null;
  /** Active perspective label (null/'' for the uncurated "All" perspective) — curated KPI/column
   *  sub-views must stamp themselves on the export, e.g. "Perspective: Intake & payments". */
  perspective?: string | null;
  /** Active group-by dimension label (null/'' when the grid is ungrouped or on the tab default). */
  group?: string | null;
  /** Rows in the exported table (after every filter). */
  visible: number;
  /** Rows in the full tab scope (before search/window/KPI). */
  total: number;
}

export function exportProvenance(d: ExportViewDesc): string {
  const labels = ((d.kpiLabels && d.kpiLabels.length ? d.kpiLabels : [d.kpiLabel || '']) as string[])
    .map(s => (s || '').trim()).filter(Boolean);
  const kpi = labels.join(' + '), q = (d.search || '').trim(), win = (d.windowLabel || '').trim();
  const persp = (d.perspective || '').trim(), grp = (d.group || '').trim();
  if (!kpi && !q && !win) {
    // Grouping/perspective alone don't NARROW the table, but they still shape it — stamp them.
    return [persp && `Perspective: ${persp}`, grp && `Grouped by ${grp}`].filter(Boolean).join(' · ');
  }
  const fmt = (n: number) => n.toLocaleString('en-US');
  const parts = persp ? [`Perspective: ${persp}`] : [];
  parts.push(`View filtered${kpi ? ': ' + kpi : ''}`);
  if (q) parts.push(`search “${q}”`);
  parts.push(`${fmt(d.visible)} of ${fmt(d.total)} rows`);
  if (win) parts.push(`window: ${win}`);
  if (grp) parts.push(`grouped by ${grp}`);
  return parts.join(' · ');
}
